'use client';

import { c as _c } from "react/compiler-runtime";
import { useDotEnv } from '@repo/dot-env/client';
import { GoogleLocationRow } from '@repo/features/location';
import { type Coordinates, getPlaceDetails, type GoogleLocationResult, type PastedLocationResult, useNearbyBias, usePastedGoogleMapsLocation, useSearchLocation } from '@repo/geolocation';
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from '@repo/ui/components/ui/combobox';
import { InputGroupAddon } from '@repo/ui/components/ui/input-group';
import { Skeleton } from '@repo/ui/components/ui/skeleton';
import { Search } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { useEffect, useState } from 'react';
export type PlaceDetails = {
  address?: string;
  city: string;
  country: string;
  country_code: string;
  lat: string;
  lng: string;
  name: string;
  place_id: string;
  place_name: string;
  postcode: string;
};
type LocationSearchStepProps = {
  className?: string;
  initialQuery?: string;
  mockIsPending?: boolean;
  mockResults?: Array<GoogleLocationResult>;
  onCoordinatesSelected?: (_coordinates: Coordinates) => void;
  onManualEntry: (_query?: string) => void;
  onPlaceSelected: (_place: PlaceDetails) => void;
  resolvePlaceDetails?: typeof getPlaceDetails;
};
type PastedLocationItem = (GoogleLocationResult | PinnedCoordinatesItem) & {
  pastedLocation: PastedLocationResult;
};
type PinnedCoordinatesItem = {
  place_id: string;
  place_name: string;
};
type SearchItem = GoogleLocationResult | PastedLocationItem;
const mapPastedLocationToItem = (location: PastedLocationResult): PastedLocationItem => {
  if (location.kind === 'google-location') {
    return {
      ...location.googlePlace,
      pastedLocation: location
    };
  }
  return {
    pastedLocation: location,
    place_id: `pinned-${location.coordinates.lat}-${location.coordinates.lng}`,
    place_name: `${location.coordinates.lat}, ${location.coordinates.lng}`
  };
};
export const LocationSearchStep = t0 => {
  const $ = _c(62);
  const {
    className,
    initialQuery: t1,
    mockIsPending,
    mockResults,
    onCoordinatesSelected,
    onManualEntry,
    onPlaceSelected,
    resolvePlaceDetails: t2
  } = t0;
  const initialQuery = t1 === undefined ? "" : t1;
  const resolvePlaceDetails = t2 === undefined ? getPlaceDetails : t2;
  const {
    GEOLOCATION_SERVICE_URL
  } = useDotEnv();
  const [inputValue, setInputValue] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);
  const [selectedValue, setSelectedValue] = useState(null);
  const isMocked = mockResults !== undefined || mockIsPending !== undefined;
  let t3;
  if ($[0] !== inputValue || $[1] !== resolvePlaceDetails) {
    t3 = {
      query: inputValue,
      resolvePlaceDetails
    };
    $[0] = inputValue;
    $[1] = resolvePlaceDetails;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    isLoading: isPastedLocationLoading,
    isPastedInput,
    mapsLabel,
    pastedLocation
  } = usePastedGoogleMapsLocation(t3);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const t4 = !isMocked && isSearchFocused;
  let t5;
  if ($[3] !== t4) {
    t5 = {
      enabled: t4
    };
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const bias = useNearbyBias(t5);
  const t6 = !isMocked && !isPastedInput;
  let t7;
  if ($[5] !== query) {
    t7 = query.trim();
    $[5] = query;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== bias || $[8] !== t6 || $[9] !== t7) {
    t8 = {
      bias,
      enabled: t6,
      query: t7
    };
    $[7] = bias;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const hookResult = useSearchLocation(t8);
  const isPending = mockIsPending ?? hookResult.isPending;
  let t9;
  if ($[11] !== hookResult || $[12] !== mockResults) {
    t9 = mockResults ?? hookResult.data ?? [];
    $[11] = hookResult;
    $[12] = mockResults;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const regularData = t9;
  let t10;
  if ($[14] !== pastedLocation) {
    t10 = pastedLocation ? mapPastedLocationToItem(pastedLocation) : null;
    $[14] = pastedLocation;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const pastedItem = t10;
  let data = regularData;
  if (isPastedInput) {
    let t11;
    if ($[16] !== pastedItem) {
      t11 = pastedItem ? [pastedItem] : [];
      $[16] = pastedItem;
      $[17] = t11;
    } else {
      t11 = $[17];
    }
    data = t11;
  }
  let t11;
  let t12;
  if ($[18] !== inputValue) {
    t11 = () => {
      const timeout = setTimeout(() => {
        setQuery(inputValue);
      }, 500);
      return () => clearTimeout(timeout);
    };
    t12 = [inputValue];
    $[18] = inputValue;
    $[19] = t11;
    $[20] = t12;
  } else {
    t11 = $[19];
    t12 = $[20];
  }
  useEffect(t11, t12);
  let t13;
  if ($[21] !== GEOLOCATION_SERVICE_URL || $[22] !== onCoordinatesSelected || $[23] !== onPlaceSelected || $[24] !== resolvePlaceDetails) {
    t13 = async value => {
      if (!value?.place_id) {
        return;
      }
      setSelectedValue(value);
      const selectedPastedLocation = "pastedLocation" in value ? value.pastedLocation : undefined;
      if (selectedPastedLocation?.kind === "pinned-coordinates") {
        onCoordinatesSelected?.(selectedPastedLocation.coordinates);
        return;
      }
      const pinnedCoordinates = selectedPastedLocation?.coordinates;
      const details = await resolvePlaceDetails(value.place_id, GEOLOCATION_SERVICE_URL);
      if (details.status !== "found") {
        onPlaceSelected({
          city: "",
          country: "",
          country_code: "",
          lat: pinnedCoordinates ? String(pinnedCoordinates.lat) : "",
          lng: pinnedCoordinates ? String(pinnedCoordinates.lng) : "",
          name: value.place_name,
          place_id: value.place_id,
          place_name: value.place_name,
          postcode: ""
        });
        return;
      }
      const {
        address
      } = details.value;
      onPlaceSelected({
        address: address.addressLine,
        city: address.city,
        country: address.countryCode,
        country_code: address.countryCode,
        lat: String(pinnedCoordinates?.lat ?? address.lat),
        lng: String(pinnedCoordinates?.lng ?? address.lng),
        name: address.name || value.place_name,
        place_id: value.place_id,
        place_name: value.place_name,
        postcode: address.postcode
      });
    };
    $[21] = GEOLOCATION_SERVICE_URL;
    $[22] = onCoordinatesSelected;
    $[23] = onPlaceSelected;
    $[24] = resolvePlaceDetails;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const handleSelect = t13;
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = (nextValue, eventDetails) => {
      if (eventDetails.reason === "input-change") {
        setInputValue(nextValue);
      }
    };
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const handleInputValueChange = t14;
  let t15;
  if ($[27] !== handleSelect) {
    t15 = nextValue_0 => {
      if (!nextValue_0) {
        setSelectedValue(null);
        return;
      }
      setInputValue(nextValue_0.place_name);
      handleSelect(nextValue_0);
    };
    $[27] = handleSelect;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const handleValueChange = t15;
  const showLoading = isPastedInput ? isPastedLocationLoading : isPending && query.length > 2;
  const showEmpty = !isPastedInput && !isPending && query.length > 2 && data.length === 0;
  const showItems = isPastedInput ? !isPastedLocationLoading && data.length > 0 : query.length > 2 && data.length > 0;
  let t16;
  if ($[29] !== className) {
    t16 = cn("flex flex-col gap-4", className);
    $[29] = className;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setIsSearchFocused(true);
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ComboboxInput aria-label="Search for a location" onFocus={t17} placeholder="Type to search for a location using Google Places" showClear={false} showTrigger={false} data-sentry-element="ComboboxInput" data-sentry-source-file="location-search-step.tsx"><InputGroupAddon align="inline-start" data-sentry-element="InputGroupAddon" data-sentry-source-file="location-search-step.tsx"><Search aria-hidden={true} className="text-muted-foreground size-4" data-sentry-element="Search" data-sentry-source-file="location-search-step.tsx" /></InputGroupAddon></ComboboxInput>;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== showLoading) {
    t19 = showLoading && <div className="p-1"><Skeleton className="h-12 w-full" /></div>;
    $[33] = showLoading;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== data || $[36] !== showItems) {
    t20 = showItems && data.map(_temp3);
    $[35] = data;
    $[36] = showItems;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t19 || $[39] !== t20) {
    t21 = <ComboboxList data-sentry-element="ComboboxList" data-sentry-source-file="location-search-step.tsx">{t19}{t20}</ComboboxList>;
    $[38] = t19;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== showEmpty) {
    t22 = showEmpty && <ComboboxEmpty>No locations found.</ComboboxEmpty>;
    $[41] = showEmpty;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== t21 || $[44] !== t22) {
    t23 = <ComboboxContent alignOffset={-24} data-sentry-element="ComboboxContent" data-sentry-source-file="location-search-step.tsx">{t21}{t22}</ComboboxContent>;
    $[43] = t21;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== handleValueChange || $[47] !== inputValue || $[48] !== selectedValue || $[49] !== t23) {
    t24 = <Combobox filter={null} inputValue={inputValue} isItemEqualToValue={_temp} itemToStringLabel={_temp2} onInputValueChange={handleInputValueChange} onValueChange={handleValueChange} value={selectedValue} data-sentry-element="Combobox" data-sentry-source-file="location-search-step.tsx">{t18}{t23}</Combobox>;
    $[46] = handleValueChange;
    $[47] = inputValue;
    $[48] = selectedValue;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== inputValue || $[52] !== isPastedInput || $[53] !== mapsLabel || $[54] !== onManualEntry) {
    t25 = <p className="text-muted-foreground text-sm">Find a location or{" "}<button className="text-primary inline underline underline-offset-4" onClick={() => onManualEntry(isPastedInput ? mapsLabel : inputValue)} type="button">add one manually</button></p>;
    $[51] = inputValue;
    $[52] = isPastedInput;
    $[53] = mapsLabel;
    $[54] = onManualEntry;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t24 || $[57] !== t25) {
    t26 = <div className="flex flex-col gap-1.5">{t24}{t25}</div>;
    $[56] = t24;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t16 || $[60] !== t26) {
    t27 = <div className={t16} data-sentry-component="LocationSearchStep" data-sentry-source-file="location-search-step.tsx">{t26}</div>;
    $[59] = t16;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  return t27;
};
function _temp(a, b) {
  return a.place_id === b.place_id;
}
function _temp2(item) {
  return item.place_name;
}
function _temp3(item_0) {
  return <ComboboxItem className="items-start py-2" key={item_0.place_id} value={item_0}>{"formatted_address" in item_0 ? <GoogleLocationRow place={item_0} /> : item_0.place_name}</ComboboxItem>;
}